import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Award, ChevronDown, ChevronRight, Settings, Save, Download,
  Users, IndianRupee, Edit2, Check, X, AlertCircle, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { toast } from 'sonner';

const api = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL + '/api' });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const fmt = (v) => `₹${(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Generate last 6 months + current month
const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      label: d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }),
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      start: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`,
      end: i === 0
        ? now.toISOString().split('T')[0]
        : new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().split('T')[0],
      isCurrent: i === 0
    });
  }
  return months;
};

const IncentivePage = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [monthOptions] = useState(getMonthOptions);
  const [selectedMonth, setSelectedMonth] = useState(getMonthOptions()[0].value);
  const [customMonth, setCustomMonth] = useState('');
  const [expandedTherapist, setExpandedTherapist] = useState(null);
  const [configModal, setConfigModal] = useState(false);
  const [configForm, setConfigForm] = useState({});
  const [savingConfig, setSavingConfig] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);
  const [trendData, setTrendData] = useState(null);

  const getDateRange = useCallback(() => {
    // Check if custom month is set
    if (customMonth) {
      const [y, m] = customMonth.split('-');
      const start = `${y}-${m}-01`;
      const end = new Date(parseInt(y), parseInt(m), 0).toISOString().split('T')[0];
      return { start, end };
    }
    const found = monthOptions.find(m => m.value === selectedMonth);
    if (found) return { start: found.start, end: found.end };
    return { start: monthOptions[0].start, end: monthOptions[0].end };
  }, [selectedMonth, customMonth, monthOptions]);

  const fetchData = useCallback(async () => {
    const { start, end } = getDateRange();
    setLoading(true);
    try {
      const res = await api.get('/incentives/report', { params: { start_date: start, end_date: end } });
      setData(res.data);
      const form = {};
      (res.data.service_configs || []).forEach(s => {
        form[s.service_id] = s.incentive_amount || 0;
      });
      setConfigForm(form);
    } catch (err) {
      toast.error('Failed to fetch incentive data');
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  const fetchTrend = useCallback(async () => {
    try {
      const res = await api.get('/incentives/monthly-trend', { params: { months: 3 } });
      setTrendData(res.data);
    } catch {
      // Trend is supplementary, don't block
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchTrend();
  }, [fetchTrend]);

  const handleMonthClick = (val) => {
    setCustomMonth('');
    setSelectedMonth(val);
  };

  const handleCustomMonth = (val) => {
    setCustomMonth(val);
    setSelectedMonth('');
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const configs = Object.entries(configForm).map(([service_id, incentive_amount]) => ({
        service_id,
        incentive_amount: parseFloat(incentive_amount) || 0
      }));
      await api.post('/incentives/config/bulk', configs);
      toast.success('Incentive configuration saved');
      setConfigModal(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const saveOverride = async (jobCardId, therapistId) => {
    setSavingOverride(true);
    try {
      await api.put('/incentives/override', {
        job_card_id: jobCardId,
        therapist_id: therapistId,
        override_amount: parseFloat(editValue) || 0
      });
      toast.success('Incentive updated');
      setEditingRow(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to update incentive');
    } finally {
      setSavingOverride(false);
    }
  };

  const exportCSV = () => {
    if (!data || !data.therapists?.length) return;
    const { start, end } = getDateRange();
    const rows = [['Therapist', 'Patient', 'Service', 'Date', 'Shared With', 'Base Incentive', 'Actual Incentive', 'Status']];
    data.therapists.forEach(t => {
      t.procedures.forEach(p => {
        rows.push([
          t.therapist_name, p.patient_name, p.service_name, p.scheduled_date,
          p.therapist_count > 1 ? `Split ${p.therapist_count} ways` : 'Solo',
          p.base_incentive, p.incentive_amount, p.is_overridden ? 'Modified' : 'Default'
        ]);
      });
      rows.push([t.therapist_name, '', '', '', 'TOTAL', '', t.total_incentive, '']);
    });
    rows.push(['', '', '', '', 'GRAND TOTAL', '', data.grand_total, '']);
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incentive_report_${start}_${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Get trend for a therapist
  const getTherapistTrend = (therapistName) => {
    if (!trendData) return null;
    const key = therapistName.trim().toLowerCase();
    const entry = trendData.therapists?.[key];
    if (!entry) return null;
    const labels = trendData.month_labels || [];
    const values = labels.map(l => entry.months?.[l] || 0);
    return { labels, values };
  };

  const TrendBadge = ({ trend }) => {
    if (!trend || trend.values.length < 2) return null;
    const current = trend.values[0];
    const previous = trend.values[1];
    const diff = current - previous;
    const pct = previous > 0 ? Math.round((diff / previous) * 100) : 0;

    return (
      <div className="flex items-center gap-1.5 ml-2">
        {trend.labels.map((label, i) => (
          <span key={label} className="text-[10px] text-gray-400 flex flex-col items-center">
            <span className={`text-xs font-semibold ${i === 0 ? 'text-amber-700' : 'text-gray-500'}`}>
              {fmt(trend.values[i])}
            </span>
            <span>{label}</span>
          </span>
        )).reverse()}
        {diff !== 0 && (
          <span className={`flex items-center text-[10px] font-semibold ml-1 ${diff > 0 ? 'text-green-600' : 'text-red-500'}`}>
            {diff > 0 ? <TrendingUp className="w-3 h-3 mr-0.5" /> : <TrendingDown className="w-3 h-3 mr-0.5" />}
            {diff > 0 ? '+' : ''}{pct}%
          </span>
        )}
        {diff === 0 && previous > 0 && (
          <span className="flex items-center text-[10px] font-semibold text-gray-400 ml-1">
            <Minus className="w-3 h-3 mr-0.5" />0%
          </span>
        )}
      </div>
    );
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 space-y-6" data-testid="incentive-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="incentive-title">
              <Award className="w-7 h-7 text-amber-500" />
              Therapist Incentives
            </h1>
            <p className="text-sm text-gray-500 mt-1">Track and manage therapist incentives based on completed procedures</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setConfigModal(true)} data-testid="config-incentives-btn">
              <Settings className="w-4 h-4 mr-1" /> Configure
            </Button>
            <Button variant="outline" size="sm" onClick={exportCSV} disabled={!data?.therapists?.length} data-testid="export-csv-btn">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Month Selector */}
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center gap-2 flex-wrap">
              {monthOptions.map((m) => (
                <Button
                  key={m.value}
                  size="sm"
                  variant={selectedMonth === m.value && !customMonth ? 'default' : 'outline'}
                  onClick={() => handleMonthClick(m.value)}
                  className={`text-xs ${selectedMonth === m.value && !customMonth ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}`}
                  data-testid={`month-btn-${m.value}`}
                >
                  {m.label}
                  {m.isCurrent && <span className="ml-1 text-[9px] opacity-70">(Current)</span>}
                </Button>
              ))}
              <div className="flex items-center gap-1 ml-2 border-l pl-2">
                <Label className="text-xs text-gray-400 whitespace-nowrap">Other:</Label>
                <Input
                  type="month"
                  value={customMonth}
                  onChange={(e) => handleCustomMonth(e.target.value)}
                  className="h-8 w-36 text-xs"
                  data-testid="custom-month-picker"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        {data && !loading && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <IndianRupee className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Grand Total Incentive</p>
                    <p className="text-xl font-bold text-gray-900" data-testid="grand-total">{fmt(data.grand_total)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Active Therapists</p>
                    <p className="text-xl font-bold text-gray-900" data-testid="therapist-count">{data.therapists?.length || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Award className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Procedures</p>
                    <p className="text-xl font-bold text-gray-900" data-testid="total-procedures">
                      {data.therapists?.reduce((sum, t) => sum + (t.procedure_count || 0), 0) || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
            <span className="ml-3 text-gray-500">Loading incentive data...</span>
          </div>
        )}

        {/* No Data */}
        {!loading && data && data.therapists?.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-lg font-medium">No procedures found</p>
              <p className="text-gray-400 text-sm mt-1">No completed job cards found for the selected month.</p>
              <p className="text-gray-400 text-sm mt-1">Make sure incentive amounts are configured for services.</p>
            </CardContent>
          </Card>
        )}

        {/* Therapist Breakdown */}
        {!loading && data && data.therapists?.map((therapist) => {
          const trend = getTherapistTrend(therapist.therapist_name);
          return (
          <Card key={therapist.therapist_id} className="overflow-hidden" data-testid={`therapist-card-${therapist.therapist_id}`}>
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition"
              onClick={() => setExpandedTherapist(expandedTherapist === therapist.therapist_id ? null : therapist.therapist_id)}
              data-testid={`therapist-toggle-${therapist.therapist_id}`}
            >
              <div className="flex items-center gap-3">
                {expandedTherapist === therapist.therapist_id ?
                  <ChevronDown className="w-5 h-5 text-gray-400" /> :
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                }
                <div className="p-2 bg-amber-50 rounded-full">
                  <Award className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <div className="flex items-center flex-wrap gap-1">
                    <p className="font-semibold text-gray-900">{therapist.therapist_name}</p>
                    <span className="text-xs text-gray-400">({therapist.procedure_count} procedure{therapist.procedure_count !== 1 ? 's' : ''})</span>
                  </div>
                  {/* Last 3 months trend */}
                  {trend && (
                    <div className="flex items-center gap-1 mt-0.5">
                      <TrendBadge trend={trend} />
                    </div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-amber-600" data-testid={`therapist-total-${therapist.therapist_id}`}>
                  {fmt(therapist.total_incentive)}
                </p>
              </div>
            </div>

            {expandedTherapist === therapist.therapist_id && (
              <div className="border-t">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600 text-xs uppercase">
                        <th className="text-left py-2 px-4">Date</th>
                        <th className="text-left py-2 px-4">Patient</th>
                        <th className="text-left py-2 px-4">Service</th>
                        <th className="text-center py-2 px-4">Shared</th>
                        <th className="text-right py-2 px-4">Base</th>
                        <th className="text-right py-2 px-4">Incentive</th>
                        <th className="text-center py-2 px-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {therapist.procedures.map((proc) => {
                        const rowKey = `${proc.job_card_id}_${therapist.therapist_id}`;
                        const isEditing = editingRow === rowKey;
                        return (
                          <tr key={rowKey} className="border-t hover:bg-amber-50/30 transition" data-testid={`procedure-row-${rowKey}`}>
                            <td className="py-2.5 px-4 text-gray-600">{formatDate(proc.scheduled_date)}</td>
                            <td className="py-2.5 px-4 font-medium text-gray-800">{proc.patient_name}</td>
                            <td className="py-2.5 px-4 text-gray-600">{proc.service_name}</td>
                            <td className="py-2.5 px-4 text-center">
                              {proc.therapist_count > 1 ? (
                                <div>
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                    {proc.therapist_count}-way split
                                  </Badge>
                                  {proc.split_with?.length > 0 && (
                                    <p className="text-[10px] text-gray-500 mt-0.5">
                                      with {proc.split_with.join(', ')}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">Solo</span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 text-right text-gray-500">{fmt(proc.base_incentive)}</td>
                            <td className="py-2.5 px-4 text-right">
                              {isEditing ? (
                                <Input
                                  type="number"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="w-24 h-7 text-right text-sm ml-auto"
                                  autoFocus
                                  data-testid={`edit-input-${rowKey}`}
                                />
                              ) : (
                                <span className={`font-semibold ${proc.is_overridden ? 'text-orange-600' : 'text-gray-900'}`}>
                                  {fmt(proc.incentive_amount)}
                                  {proc.is_overridden && (
                                    <Badge variant="outline" className="ml-1 text-[10px] bg-orange-50 text-orange-600 border-orange-200">
                                      edited
                                    </Badge>
                                  )}
                                </span>
                              )}
                            </td>
                            <td className="py-2.5 px-4 text-center">
                              {isEditing ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => saveOverride(proc.job_card_id, therapist.therapist_id)}
                                    disabled={savingOverride}
                                    data-testid={`save-override-${rowKey}`}
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                    onClick={() => setEditingRow(null)}
                                    data-testid={`cancel-edit-${rowKey}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-gray-400 hover:text-amber-600"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingRow(rowKey);
                                    setEditValue(proc.incentive_amount);
                                  }}
                                  data-testid={`edit-btn-${rowKey}`}
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Therapist Total Row */}
                      <tr className="bg-amber-50/60 border-t-2 border-amber-200">
                        <td colSpan={5} className="py-2.5 px-4 text-right font-semibold text-gray-700">
                          Total for {therapist.therapist_name}
                        </td>
                        <td className="py-2.5 px-4 text-right font-bold text-amber-700">{fmt(therapist.total_incentive)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>
          );
        })}

        {/* Grand Total Footer */}
        {!loading && data && data.therapists?.length > 0 && (
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-700">Grand Total Incentive</span>
                <span className="text-2xl font-bold text-amber-700" data-testid="grand-total-footer">{fmt(data.grand_total)}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Configuration Modal */}
        <Dialog open={configModal} onOpenChange={setConfigModal}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="config-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-600" />
                Service Incentive Configuration
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500 -mt-2">Set the fixed incentive amount per service. When multiple therapists share a job card, this amount is split equally.</p>
            <div className="space-y-3 mt-4">
              {data?.service_configs?.map((svc) => (
                <div key={svc.service_id} className="flex items-center justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
                  <Label className="text-sm font-medium text-gray-700 flex-1">{svc.service_name}</Label>
                  <div className="flex items-center gap-1.5">
                    <span className="text-gray-400 text-sm">₹</span>
                    <Input
                      type="number"
                      min="0"
                      value={configForm[svc.service_id] || ''}
                      onChange={(e) => setConfigForm(prev => ({ ...prev, [svc.service_id]: e.target.value }))}
                      className="w-28 h-8 text-right text-sm"
                      placeholder="0"
                      data-testid={`config-input-${svc.service_id}`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setConfigModal(false)}>Cancel</Button>
              <Button onClick={saveConfig} disabled={savingConfig} data-testid="save-config-btn">
                <Save className="w-4 h-4 mr-1" /> {savingConfig ? 'Saving...' : 'Save Configuration'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default IncentivePage;
